import React from "react";
import {
  Box, Typography, Button, Popover, Divider,
} from "@mui/material";
import { MapPin, Timer, ChevronDown, ChevronUp } from "lucide-react";

import { TransportModal } from "./TransportModal";

export const AREA_PRESETS = {
  helsinki:  { coordinates: { lat: 60.1699, long: 24.9384 }, city: "Helsinki"  },
  tampere:   { coordinates: { lat: 61.4978, long: 23.7610 }, city: "Tampere"   },
  lahti:     { coordinates: { lat: 60.9827, long: 25.6612 }, city: "Lahti"     },
  kuopio:    { coordinates: { lat: 62.8924, long: 27.6772 }, city: "Kuopio"    },
  jyvaskyla: { coordinates: { lat: 62.2415, long: 25.7209 }, city: "Jyväskylä" },
};

export const TRANSPORT_PRESETS = {
  bussinvaihto: "wait",
  henkiloauto:  "express",
  pakettiauto:  "van",
};

export const TRANSPORT_LABELS = {
  wait: "Bussinvaihto",
  express:  "Henkilöauto",
  van:  "Pakettiauto",
};

const SORT_LABELS = { urgency: "Aika", reward: "Palkkio" };

export function ActiveBar({
  areaLabel,
  alueParam,
  transportFilter,
  sort,
  isSpecialCourier,
  colors,
  onSetSearchParams,
  onSetCourierLocation,
  onSortChange,
}) {
  const [areaAnchor,         setAreaAnchor]         = React.useState(null);
  const [sortAnchor,         setSortAnchor]         = React.useState(null);
  const [transportModalOpen, setTransportModalOpen] = React.useState(false);
  const [collapsed,          setCollapsed]          = React.useState(false);

  const transportLabel = transportFilter
    ? (TRANSPORT_LABELS[transportFilter] ?? transportFilter)
    : "Kaikki";

  const valueSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    p: 0,
    minWidth: 0,
    lineHeight: 1.3,
    justifyContent: "flex-start",
    color: "text.primary",
  };

  return (
    <>
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 1.25, sm: 1 },
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderBottom: isSpecialCourier
            ? `1px solid ${colors.surface?.border || "rgba(0,0,0,0.08)"}`
            : "none",
        }}
      >
        {/* ── Collapse toggle ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setCollapsed((c) => !c)}
            endIcon={collapsed ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              color: "text.secondary",
              fontWeight: 500,
              p: 0.25,
              minWidth: 0,
            }}
          >
            {collapsed ? "Näytä suodattimet" : "Piilota"}
          </Button>
        </Box>

        {/* ── Filters row (collapsible) ── */}
        {!collapsed && (
          <Box sx={{ display: "flex", alignItems: "stretch", gap: { xs: 1, sm: 2 } }}>

            {/* Alue */}
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MapPin size={13} style={{ opacity: 0.45 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Alue
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                endIcon={<ChevronDown size={13} />}
                onClick={(e) => setAreaAnchor(e.currentTarget)}
                sx={valueSx}
              >
                {areaLabel}
              </Button>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Kuljetustapa */}
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Kuljetustapa
              </Typography>
              <Button
                size="small"
                variant="text"
                endIcon={<ChevronDown size={13} />}
                onClick={() => setTransportModalOpen(true)}
                sx={valueSx}
              >
                {transportLabel}
              </Button>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Järjestys */}
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Timer size={13} style={{ opacity: 0.45 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Järjestys
                </Typography>
              </Box>
              <Button
                size="small"
                variant="text"
                endIcon={<ChevronDown size={13} />}
                onClick={(e) => setSortAnchor(e.currentTarget)}
                sx={valueSx}
              >
                {SORT_LABELS[sort]}
              </Button>
            </Box>

          </Box>
        )}
      </Box>

      {/* ── Area popover ── */}
      <Popover
        open={Boolean(areaAnchor)}
        anchorEl={areaAnchor}
        onClose={() => setAreaAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75, minWidth: 180 }}>
          <Button
            size="medium"
            variant={!alueParam ? "contained" : "outlined"}
            disableElevation
            onClick={() => {
              onSetSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                p.delete("alue");
                return p;
              });
              setAreaAnchor(null);
            }}
            sx={{ textTransform: "none", fontWeight: 600, justifyContent: "flex-start" }}
          >
            Suomi (kaikki)
          </Button>
          {Object.entries(AREA_PRESETS).map(([slug, { city }]) => (
            <Button
              key={slug}
              size="medium"
              variant={alueParam === slug ? "contained" : "outlined"}
              disableElevation
              onClick={() => {
                onSetSearchParams((prev) => {
                  const p = new URLSearchParams(prev);
                  p.set("alue", slug);
                  return p;
                });
                onSetCourierLocation(AREA_PRESETS[slug]);
                setAreaAnchor(null);
              }}
              sx={{ textTransform: "none", fontWeight: 600, justifyContent: "flex-start" }}
            >
              {city}
            </Button>
          ))}
        </Box>
      </Popover>

      {/* ── Sort popover ── */}
      <Popover
        open={Boolean(sortAnchor)}
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75, minWidth: 180 }}>
          {[
            { value: "urgency", icon: <Timer size={15} />, label: "Aika"    },
            { value: "reward",  icon: <Timer size={15} />, label: "Palkkio" },
          ].map(({ value, icon, label }) => (
            <Button
              key={value}
              size="medium"
              variant={sort === value ? "contained" : "outlined"}
              disableElevation
              startIcon={icon}
              onClick={() => { onSortChange(value); setSortAnchor(null); }}
              sx={{ textTransform: "none", fontWeight: 600, justifyContent: "flex-start" }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Popover>

      {/* ── Transport modal ── */}
      <TransportModal
        open={transportModalOpen}
        onClose={() => setTransportModalOpen(false)}
        value={transportFilter}
        onChange={(type) => {
          onSetSearchParams((prev) => {
            const p = new URLSearchParams(prev);
            const slug = Object.entries(TRANSPORT_PRESETS).find(([, v]) => v === type)?.[0];
            if (slug) p.set("kuljetustapa", slug);
            else p.delete("kuljetustapa");
            return p;
          });
        }}
      />
    </>
  );
}